import useEnv from "@/src/useEnv";
import { useFlag } from "@/src/useFlag";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import createPersistedState from "@/src/createPersistedState";

const NEUTRAL_PATHS = ["/settings", "/user-settings"];
export const AGENT_BASE_URL = "/agent-app";
export const AGENT_LANDING_PATH = `${AGENT_BASE_URL}/dashboard`;
export const APPS_LANDING_PATH = "/dashboard";

export type Product = "apps" | "agents";
const useProductState = createPersistedState<Product>("product");

export function useProductMode() {
  const router = useRouter();
  const [product, setProduct] = useProductState("apps");
  const showAgentsProductOnly = useFlag("oso-for-agents-ui");
  const showBothAppsAndAgentsProduct = useFlag("oso-for-apps-and-agents-ui");

  // Sync persisted state with route.
  // This ensures that if we navigate to a neutral page (e.g. Settings),
  // we remember that we were just in Agents mode.
  useEffect(() => {
    const isOnAgentPage = router.pathname.startsWith(AGENT_BASE_URL);
    if (isOnAgentPage && product !== "agents") {
      setProduct("agents");
    }
  }, [router.pathname, product, setProduct]);

  const activeProduct = useMemo(() => {
    // The `showAgentsProductOnly` flag intentionally takes precedence over `showBothAppsAndAgentsProduct`.
    // When enabled, we only show the Agents product.
    if (showAgentsProductOnly) return "agents";

    // /agent-app/* is determined by URL alone so first render is correct
    // before the tenant/env-derived flags have loaded. Apps-only users who
    // land here are redirected by useProductModeGuard below.
    if (router.pathname.startsWith(AGENT_BASE_URL)) {
      return "agents";
    }

    // Neutral pages have no implicit product — both-product users fall back
    // to their persisted preference.
    if (
      showBothAppsAndAgentsProduct &&
      NEUTRAL_PATHS.includes(router.pathname)
    ) {
      return product;
    }

    // All other pages are apps
    return "apps";
  }, [
    product,
    showAgentsProductOnly,
    showBothAppsAndAgentsProduct,
    router.pathname,
  ]);

  return {
    product: activeProduct,
    setProduct,
    showAgentsProductOnly,
    showBothAppsAndAgentsProduct,
  };
}

/**
 * Guards routes based on feature flags:
 *
 * 1. showOsoForAppsAndAgentsFlag: User can navigate anywhere (Apps + Agents).
 *    No redirects, mode just determines which dashboard is "home".
 *
 * 2. showOnlyOsoForAgentsFlag: Agent-only user.
 *    Redirect to /agent-app/dashboard if they try to leave /agent-app/*.
 *
 * 3. Neither flag: Apps-only user.
 *    Redirect to /dashboard if they try to access /agent-app/*.
 */
export function useProductModeGuard() {
  const router = useRouter();
  const [canViewPage, setCanViewPage] = useState(false);
  const { selectedEnv } = useEnv();
  const { showAgentsProductOnly, showBothAppsAndAgentsProduct } =
    useProductMode();

  useEffect(() => {
    // Wait for environment to load before running guard
    if (!selectedEnv) {
      return;
    }

    const path = router.pathname;
    const isOnAgentPage = path.startsWith("/agent-app");

    // Allowlist: settings pages should always be accessible (even for agent-only users).
    if (NEUTRAL_PATHS.includes(path)) {
      setCanViewPage(true);
      return;
    }

    // Case 1: Agent-only user - must stay within /agent-app/*
    if (showAgentsProductOnly) {
      if (!isOnAgentPage) {
        setCanViewPage(false);
        router.replace(AGENT_LANDING_PATH);
      } else {
        setCanViewPage(true);
      }
      return;
    }

    // Case 2: User has access to both Apps and Agents - allow all navigation
    if (showBothAppsAndAgentsProduct) {
      setCanViewPage(true);
      return;
    }

    // Case 3: Apps-only user (neither flag) - cannot access /agent-app/*
    if (isOnAgentPage) {
      setCanViewPage(false);
      router.replace(APPS_LANDING_PATH);
    } else {
      setCanViewPage(true);
    }
  }, [
    router,
    selectedEnv,
    showAgentsProductOnly,
    showBothAppsAndAgentsProduct,
  ]);

  // Allow page to render while environment is loading
  if (!selectedEnv) {
    return true;
  }

  return canViewPage;
}
